/**
 * The Download action (S1: exactly one blob per job, spec §7.2). The OS save
 * dialog is the consent; a dismissed dialog is a first-class non-error outcome
 * that produces no job and no toast. Task 1.15 layers the transfers store,
 * the status-bar chip and the terminal-state toast on top of the job id this
 * returns — the IPC call stays here so both surfaces share one entry point.
 * The store learns the job's origin here (`noteJobSource`), the only fact the
 * snapshot does not carry. Each request carries a `requestId`; closing the Storage Explorer tab cancels the
 * requests it still has in main (archive preflight / picker, integration review C1) and that cancel is silent.
 */
import { toast } from '../../../../components/ui/toaster';
import type { StorageDownloadResult, StorageErrorFix, StorageItem, StorageResourceRef } from '../../../../types/azureStorage';
import { useTransfersStore } from '../../../transfers/store/transfersStore';
import { cancelStorageRequest, downloadStorageResources, newStorageRequestId, toStorageOperationError } from './azureStorageApi';

export type StorageDownloadOutcome =
  | { kind: 'started'; jobId: string }
  | { kind: 'dialog-cancelled' }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string; fix?: StorageErrorFix };

/** Download request ids still answering in main, per Storage Explorer tab. */
const pendingByTab = new Map<string, Set<string>>();

/** Called when a Storage Explorer tab is dropped: its unanswered download requests stop (no preflight read, picker or job follows). */
export function cancelStorageTabDownloads(tabId: string): void {
  const pending = pendingByTab.get(tabId);
  pendingByTab.delete(tabId);
  for (const requestId of pending ?? []) void cancelStorageRequest(requestId);
}

let testRequests: Array<{ connectionId: string; resources: StorageResourceRef[] }> = [];

/** Test-mode probe for the exact renderer-to-preload contract; production callers never read it. */
export const storageDownloadRequestProbe = {
  reset: (): void => { testRequests = []; },
  read: (): Array<{ connectionId: string; resources: StorageResourceRef[] }> => structuredClone(testRequests),
};

type BlobItem = Extract<StorageItem, { kind: 'blob' }>;

function pendingTarget(status: string): string {
  const target = /to[- ](hot|cool|cold)/iu.exec(status)?.[1];
  return target ? target[0].toUpperCase() + target.slice(1).toLowerCase() : 'the requested tier';
}

/**
 * S3's archive rule over S2's multi-resource download: every DIRECTLY selected blob whose listed row is archived or
 * rehydrating refuses the download before any dialog opens (main re-checks). A single blob keeps S3's exact sentence;
 * in a multi-selection the refusal names the blob. Blobs inside a selected folder or container are the job's concern.
 */
function archivedRefusal(items: readonly StorageItem[], multiple: boolean): { message: string; blob: BlobItem; archived: boolean } | undefined {
  const blob = items.find((item): item is BlobItem => item.kind === 'blob' && (!!item.archiveStatus || item.tier === 'Archive'));
  if (!blob) return undefined;
  const named = (message: string) => (multiple ? `"${blob.name}": ${message}` : message);
  return blob.archiveStatus
    ? { blob, archived: false, message: named(`Rehydration is pending (to ${pendingTarget(blob.archiveStatus)}); download once it completes.`) }
    : { blob, archived: true, message: named('This blob is archived; rehydrate it to Hot or Cool before downloading.') };
}

export async function requestStorageDownload(args: {
  connectionId: string;
  resources: StorageResourceRef[];
  /** S3: the listed rows of the directly selected blobs, so an archived / rehydrating one is refused locally. */
  items?: readonly StorageItem[];
  /** S3: the toast's "Rehydrate…" action for the archived blob it names. */
  onRehydrate?: (blob: BlobItem) => void;
  /** The Storage Explorer tab that asked: closing it cancels the request (integration review C1). */
  tabId?: string;
}): Promise<StorageDownloadOutcome> {
  const refusal = archivedRefusal(args.items ?? [], args.resources.length > 1);
  if (refusal && !refusal.archived) {
    toast.error({ title: 'Download failed', description: refusal.message });
    return { kind: 'error', message: refusal.message };
  }
  if (refusal) {
    const { blob } = refusal;
    toast.error({
      title: 'Download failed',
      description: refusal.message,
      ...(args.onRehydrate ? { action: { label: 'Rehydrate…', onClick: () => args.onRehydrate?.(blob) } } : {}),
    });
    return { kind: 'error', message: refusal.message, fix: { kind: 'rehydrate' } };
  }
  if (typeof window !== 'undefined' && window.electronAPI?.testMode?.isTestMode()) testRequests.push(structuredClone({ connectionId: args.connectionId, resources: args.resources }));
  let result: StorageDownloadResult;
  const requestId = newStorageRequestId();
  const tabPending = args.tabId ? pendingByTab.get(args.tabId) ?? new Set<string>() : undefined;
  if (args.tabId && tabPending) pendingByTab.set(args.tabId, tabPending.add(requestId));
  try {
    result = await downloadStorageResources({ connectionId: args.connectionId, resources: args.resources, requestId });
  } catch (error) {
    const typed = toStorageOperationError(error);
    if (typed.category === 'cancelled') return { kind: 'cancelled' };
    toast.error({ title: 'Download failed', description: typed.message });
    return { kind: 'error', message: typed.message };
  } finally {
    tabPending?.delete(requestId);
    if (args.tabId && tabPending?.size === 0 && pendingByTab.get(args.tabId) === tabPending) pendingByTab.delete(args.tabId);
  }
  if (!result.started) return { kind: 'dialog-cancelled' };
  // The chip / toast / analytics hang off this id; `source: 'toolbar'` is the analytics bucket, never a name or path.
  useTransfersStore.getState().noteJobSource(result.jobId, 'toolbar');
  return { kind: 'started', jobId: result.jobId };
}
